import { ArithmeticHelper } from '@helpers/arithmetic.helper';
import { UPLOAD_FILE_CONFIG } from '@shared-overlays/upload-file-overlay/upload-file-config.constant';

export class FileHelper {
  /**
   * Verify if the file size is greater than an expected size.
   *
   * @param file the file to analyze
   * @param size the maximum file size in Mo
   * @returns true if the file size is greater than the expected size, false otherwise
   */
  static isFileSizeGreaterThanSize(file: Blob, size: number): boolean {
    // the file size is in bytes
    // so we need to convert into Mo (1048576 correspond's to 1024 * 1024)
    return file.size / 1048576 > size;
  }

  /**
   * Verify if all the files sizes is greater than an expected size.
   *
   * @param files the files to analyze
   * @param size the maximum file size in Mo
   * @returns true if the files sizes is greater than the expected size, false otherwise
   */
  static isFilesSizesSumGreaterThanSize(files: Blob[], size: number, initialValue: number = 0): boolean {
    const totalFilesSize = files.reduce(
      (acc: number, file: Blob) => ArithmeticHelper.plus(acc, file.size / 1048576),
      initialValue
    );

    return totalFilesSize > size;
  }

  /**
   * Verify if the a file has an accepted type.
   *
   * @param acceptedTypes the accepted types
   * @param file the file to verify
   * @returns true is the file has an accepted type, false otherwise
   */
  static isAcceptedType(acceptedTypes: string[], file: File): boolean {
    if (acceptedTypes.length === 0) {
      return true;
    }
    return acceptedTypes.includes(file.type);
  }

  /**
   * Verify if the a file is valid
   *
   * @param file the file to verify
   * @param acceptedTypes the accepted types
   * @param size the maximum file size in Mo
   * @returns true is the file has an accepted type and size is lower than the expected size, false otherwise
   */
  static isValid(
    file: File,
    acceptedTypes: string[] = UPLOAD_FILE_CONFIG.acceptedTypes,
    size: number = UPLOAD_FILE_CONFIG.maximumSize
  ): boolean {
    return FileHelper.isAcceptedType(acceptedTypes, file) && !FileHelper.isFileSizeGreaterThanSize(file, size);
  }

  /**
   * Convert base64Url to File.
   *
   * @param url base64Url string
   * @param filename file name
   * @returns File
   */
  static base64UrlToFile = async (url: string, filename: string): Promise<File> => {
    const res = await fetch(url);
    const buf = await res.arrayBuffer();
    return new File([buf], filename);
  };

  /**
   * Convert file to base64
   *
   * @param file the file to convert
   * @returns base64 file encoding
   */
  static fileToBase64 = async (file: Blob): Promise<string | null> => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    return new Promise(resolve => {
      reader.onloadend = () => {
        const content = (reader.result as string)?.replace(/data:.*;base64,/gm, '');
        resolve(content);
      };
    });
  };

  static base64ToBlob(base64Data: string, contentType: string): Blob {
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays: Uint8Array[] = new Array<Uint8Array>(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes: number[] = new Array<number>(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: contentType });
  }

  static getInvalidFileErrorMessage(acceptedMimeTypes: string[] = UPLOAD_FILE_CONFIG.acceptedTypes): string {
    const fileExts = [...new Set(acceptedMimeTypes.map(mimeType => FileHelper.mimeTypeToFileExt(mimeType)))]
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
    return `Fichier incorrect, la taille maximale doit être de ${UPLOAD_FILE_CONFIG.maximumSize}Mo et le fichier doit être de type ${fileExts}`;
  }

  static mimeTypeToFileExt(mimeType: string): string {
    switch (mimeType) {
      case 'text/plain':
        return '.txt';
      case 'image/jpeg':
        return '.jpg/.jpeg';
      case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        return '.xlsx';
      case 'application/msword':
        return '.doc';
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return '.docx';
      case 'application/vnd.oasis.opendocument.text':
        return '.odt';
      case 'application/vnd.ms-powerpoint':
        return '.ppt';
      case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        return '.pptx';
      case 'application/vnd.ms-excel':
        return '.xls';
      case 'application/octet-stream':
        return '.pdf';
      default:
        const [_, ext] = mimeType.split('/');
        return `.${ext}`;
    }
  }
}
